import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, TrendingUp, TrendingDown, Users, DollarSign, Brain, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../lib/logger';

const SimpleLineChart = ({ data, dataKey, label, color = '#DC2626', showBounds = false }) => {
  if (!data || data.length === 0) return <div className="text-center text-slate-400 py-8">No data available</div>;
  
  const values = data.map(d => d[dataKey] || 0);
  const maxValue = Math.max(...values, ...data.map(d => d.upper_bound || 0));
  const minValue = Math.min(...values, 0);
  const range = maxValue - minValue || 1;
  
  const width = 100;
  const height = 60;
  const padding = 5;
  
  // Generate SVG path
  const points = data.map((item, index) => {
    const x = (index / (data.length - 1 || 1)) * (width - 2 * padding) + padding;
    const y = height - padding - ((item[dataKey] - minValue) / range) * (height - 2 * padding);
    return `${x},${y}`;
  }).join(' ');
  
  // Confidence bounds if available
  let upperBoundPoints = '';
  let lowerBoundPoints = '';
  
  if (showBounds && data[0]?.upper_bound !== undefined) {
    upperBoundPoints = data.map((item, index) => {
      const x = (index / (data.length - 1 || 1)) * (width - 2 * padding) + padding;
      const y = height - padding - ((item.upper_bound - minValue) / range) * (height - 2 * padding);
      return `${x},${y}`;
    }).join(' ');
    
    lowerBoundPoints = data.map((item, index) => {
      const x = (index / (data.length - 1 || 1)) * (width - 2 * padding) + padding;
      const y = height - padding - ((item.lower_bound - minValue) / range) * (height - 2 * padding);
      return `${x},${y}`;
    }).join(' ');
  }
  
  return (
    <div className="space-y-3">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-40">
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map(percent => (
          <line
            key={percent}
            x1={padding}
            y1={height - padding - (percent / 100) * (height - 2 * padding)}
            x2={width - padding}
            y2={height - padding - (percent / 100) * (height - 2 * padding)}
            stroke="#e2e8f0"
            strokeWidth="0.2"
          />
        ))}
        
        {/* Confidence bounds area */}
        {showBounds && upperBoundPoints && (
          <>
            <polygon
              points={`${lowerBoundPoints.split(' ').reverse().join(' ')} ${upperBoundPoints}`}
              fill={color}
              opacity="0.1"
            />
            <polyline points={upperBoundPoints} fill="none" stroke={color} strokeWidth="0.5" strokeDasharray="2,2" opacity="0.5" />
            <polyline points={lowerBoundPoints} fill="none" stroke={color} strokeWidth="0.5" strokeDasharray="2,2" opacity="0.5" />
          </>
        )}
        
        {/* Main line */}
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Area under line */}
        <polygon
          points={`${padding},${height - padding} ${points} ${width - padding},${height - padding}`}
          fill={color}
          opacity="0.15"
        />
        
        {/* Data points */}
        {data.map((item, index) => {
          const x = (index / (data.length - 1 || 1)) * (width - 2 * padding) + padding;
          const y = height - padding - ((item[dataKey] - minValue) / range) * (height - 2 * padding);
          return (
            <circle
              key={item.date || item.label || item[dataKey]}
              cx={x}
              cy={y}
              r="1.5"
              fill={color}
            />
          );
        })}
      </svg>
      
      {/* Labels */}
      <div className="flex justify-between text-xs text-slate-400">
        <span>{data[0]?.date?.slice(5, 10) || 'Start'}</span>
        <span className="text-purple-600 font-medium">Forecast</span>
        <span>{data[data.length - 1]?.date?.slice(5, 10) || 'End'}</span>
      </div>
    </div>
  );
};

const MLInsights = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [revenueForecast, setRevenueForecast] = useState(null);
  const [patientForecast, setPatientForecast] = useState(null);

  useEffect(() => {
    fetchForecasts();
  }, []);

  const fetchForecasts = async () => {
    setLoading(true);
    try {
      const [revenueRes, patientRes] = await Promise.all([
        api.get('/platform/forecast/revenue?days=30'),
        api.get('/platform/forecast/patients?days=30')
      ]);
      
      setRevenueForecast(revenueRes.data);
      setPatientForecast(patientRes.data);
    } catch (error) {
      logger.error('Failed to load forecasts:', error);
      toast.error('Failed to load ML forecasts');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const getConfidenceBadge = (confidence) => {
    const colors = {
      high: 'bg-green-100 text-green-800',
      medium: 'bg-amber-100 text-amber-800',
      low: 'bg-red-100 text-red-800'
    };
    return colors[confidence] || colors.medium;
  };

  return (
    <div data-testid="ml-insights-page">
      <header className="page-header glass-header">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/platform/analytics')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope] flex items-center gap-2">
              <Brain className="w-7 h-7 text-purple-600" />
              ML Insights & Forecasting
            </h1>
            <p className="text-slate-500">AI-powered predictions and trend analysis</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchForecasts}
            disabled={loading}
          >
            <Brain className="w-4 h-4 mr-2" />
            Refresh Forecasts
          </Button>
        </div>
      </header>

      <div className="page-content max-w-7xl">
        <div className="space-y-6">
          {/* Revenue Forecast */}
          {revenueForecast && (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-500" />
                      Revenue Forecast (Next 30 Days)
                    </CardTitle>
                    <CardDescription>ML-powered revenue prediction using Linear Regression</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getConfidenceBadge(revenueForecast.confidence)}>
                      {revenueForecast.confidence.toUpperCase()} CONFIDENCE
                    </Badge>
                    <Badge variant="outline">
                      R² = {revenueForecast.r2_score}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {revenueForecast.message ? (
                  <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg text-center">
                    <p className="text-amber-800 font-medium">{revenueForecast.message}</p>
                    <p className="text-sm text-amber-600 mt-2">Please ensure sufficient historical data is available for accurate forecasting.</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500">Historical Avg</p>
                        <p className="text-xl font-bold text-slate-900">₹{revenueForecast.historical_average?.toLocaleString()}</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <p className="text-sm text-purple-600">Forecast Avg</p>
                        <p className="text-xl font-bold text-purple-900">₹{revenueForecast.forecast_average?.toLocaleString()}</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-600">Trend</p>
                        <p className="text-xl font-bold text-blue-900 flex items-center gap-1">
                          {revenueForecast.trend === 'increasing' ? (
                            <><TrendingUp className="w-5 h-5 text-green-600" /> {revenueForecast.trend_percentage}%</>
                          ) : (
                            <><TrendingDown className="w-5 h-5 text-red-600" /> {revenueForecast.trend_percentage}%</>
                          )}
                        </p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-600">Training Days</p>
                        <p className="text-xl font-bold text-green-900">{revenueForecast.model_info?.training_days || 'N/A'}</p>
                      </div>
                    </div>
                    
                    <SimpleLineChart 
                      data={revenueForecast.forecast}
                      dataKey="predicted_revenue"
                      label="Revenue"
                      color="#8B5CF6"
                      showBounds={true}
                    />
                    
                    {revenueForecast.confidence === 'low' && (
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800">
                          Warning: Low confidence prediction. More historical data will improve accuracy.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Patient Forecast */}
          {patientForecast && (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-500" />
                      Patient Volume Forecast (Next 30 Days)
                    </CardTitle>
                    <CardDescription>ML-powered patient registration prediction</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getConfidenceBadge(patientForecast.confidence)}>
                      {patientForecast.confidence.toUpperCase()} CONFIDENCE
                    </Badge>
                    <Badge variant="outline">
                      R² = {patientForecast.r2_score}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {patientForecast.message ? (
                  <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg text-center">
                    <p className="text-amber-800 font-medium">{patientForecast.message}</p>
                    <p className="text-sm text-amber-600 mt-2">Please ensure sufficient historical data is available for accurate forecasting.</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500">Historical Avg</p>
                        <p className="text-xl font-bold text-slate-900">{patientForecast.historical_average} patients/day</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <p className="text-sm text-purple-600">Forecast Avg</p>
                        <p className="text-xl font-bold text-purple-900">{patientForecast.forecast_average} patients/day</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-600">Trend</p>
                        <p className="text-xl font-bold text-blue-900 flex items-center gap-1">
                          {patientForecast.trend === 'increasing' ? (
                            <><TrendingUp className="w-5 h-5 text-green-600" /> {patientForecast.trend_percentage}%</>
                          ) : (
                            <><TrendingDown className="w-5 h-5 text-red-600" /> {patientForecast.trend_percentage}%</>
                          )}
                        </p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-600">Training Days</p>
                        <p className="text-xl font-bold text-green-900">{patientForecast.model_info?.training_days || 'N/A'}</p>
                      </div>
                    </div>
                    
                    <SimpleLineChart 
                      data={patientForecast.forecast}
                      dataKey="predicted_patients"
                      label="Patients"
                      color="#3B82F6"
                      showBounds={true}
                    />
                    
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">Forecast Summary (30 days)</h4>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-blue-600">Total Expected:</span>
                          <p className="font-bold text-blue-900">
                            {patientForecast.forecast.reduce((sum, d) => sum + d.predicted_patients, 0)} patients
                          </p>
                        </div>
                        <div>
                          <span className="text-blue-600">Best Case:</span>
                          <p className="font-bold text-blue-900">
                            {patientForecast.forecast.reduce((sum, d) => sum + d.upper_bound, 0)} patients
                          </p>
                        </div>
                        <div>
                          <span className="text-blue-600">Worst Case:</span>
                          <p className="font-bold text-blue-900">
                            {patientForecast.forecast.reduce((sum, d) => sum + d.lower_bound, 0)} patients
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Model Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">About ML Forecasting</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-slate-600">
              <p>
                <strong className="text-slate-900">Model:</strong> Linear Regression with 95% confidence intervals
              </p>
              <p>
                <strong className="text-slate-900">Training Data:</strong> Last 90 days of historical data
              </p>
              <p>
                <strong className="text-slate-900">Confidence Levels:</strong>
              </p>
              <ul className="ml-4 space-y-1">
                <li><span className="text-green-600">●</span> <strong>High (R² {'>'} 0.7):</strong> Strong predictive power, reliable forecasts</li>
                <li><span className="text-amber-600">●</span> <strong>Medium (R² 0.4-0.7):</strong> Moderate accuracy, use with caution</li>
                <li><span className="text-red-600">●</span> <strong>Low (R² {'<'} 0.4):</strong> Limited accuracy, more data needed</li>
              </ul>
              <p className="pt-2 border-t text-xs text-slate-500">
                Note: Forecasts update automatically as new data is collected. Higher R² scores indicate better model fit.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MLInsights;
